import { Modal } from 'antd'
import { useState, useEffect } from 'react'

interface ChangeColdLeadsLimitModalProps {
  open: boolean
  currentLimit: number
  onClose: () => void
  onSave: (newLimit: number) => Promise<void>
}

export const ChangeColdLeadsLimitModal = ({
  open,
  currentLimit,
  onClose,
  onSave,
}: ChangeColdLeadsLimitModalProps) => {
  const [newLimit, setNewLimit] = useState<string>(currentLimit.toString())
  const [loading, setLoading] = useState(false)
  const MINIMUM_LIMIT = 100

  useEffect(() => {
    if (open) {
      setNewLimit(currentLimit.toString())
    }
  }, [open, currentLimit])

  const handleSave = async () => {
    setLoading(true)
    try {
      await onSave(Number(newLimit))
    } finally {
      setLoading(false)
    }
  }

  const numericLimit = Number(newLimit) || 0
  const estimatedLeads = Math.floor(numericLimit / 2)
  const hasMinimumError = numericLimit > 0 && numericLimit < MINIMUM_LIMIT

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title=""
      footer={null}
      className="ff-modal"
      width={840}
    >
      <div className="flex flex-col gap-6 py-6">
        {/* Title */}
        <h2 className="text-white text-2xl font-semibold">
          Change Monthly Budget for Reviving Old Leads
        </h2>

        {/* Info Box */}
        <div className="bg-[#2d2d35] rounded-xl p-6 border border-white/10">
          <p className="text-white text-base mb-2">
            It takes about $2.00 on average to fully nurture an old lead through
            its lifetime.
          </p>
          <p className="text-[#9ca3af] text-sm">
            Don't worry, Forward Flow's algorithms will prioritize high-potential
            leads first and spend your budget efficiently to maximize conversions.
          </p>
        </div>

        {/* Input Section */}
        <div className="bg-[#2d2d35] rounded-xl p-6 border border-white/10">
          <h3 className="text-white text-base font-medium mb-2">
            Provide a maximum monthly spend limit for Revive Campaigns
          </h3>
          <p className="text-[#9ca3af] text-sm mb-4">
            This is your monthly spend cap for reviving old leads through calls,
            texts, and voicemails.
          </p>
          
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-lg">
              $
            </span>
            <input
              type="number"
              min={0}
              value={newLimit}
              onChange={(e) => setNewLimit(e.target.value)}
              className="w-full bg-[#1e1e24] border border-white/10 rounded-lg pl-8 pr-24 py-3 text-white text-lg focus:outline-none focus:border-primary"
              placeholder="200"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9ca3af] text-sm">
              /Month
            </span>
          </div>

          <p className="text-[#9ca3af] text-sm italic mt-3">
            With a ${numericLimit} monthly budget, we can nurture approximately{' '}
            {estimatedLeads} cold leads each month
          </p>
          {hasMinimumError && (
            <p className="text-red-400 text-sm mt-2">
              Minimum Monthly Spend Limit should be $100
            </p>
          )}
        </div>

        {/* Refill Info */}
        <div className="bg-[#2d2d35] rounded-xl p-6 border border-white/10">
          <h3 className="text-white text-base font-medium mb-3">
            How your credits are refilled
          </h3>
          <ul className="space-y-2 text-[#9ca3af] text-sm">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>
                We'll auto-purchase $100 credits at a time whenever your wallet
                runs as low as $20.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>
                Forward Flow keeps track of your revive campaign's monthly limit -
                and stops purchases once that limit is hit.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>
                You can pause or stop anytime - unused credits will stay available
                in your account.
              </span>
            </li>
          </ul>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 bg-[#2d2d35] hover:bg-[#3d3d45] text-white rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || hasMinimumError}
            className="px-6 py-3 bg-[#8b5cf6] hover:bg-[#7c3aed] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
